import { Component, inject, OnInit, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Observable,
  debounceTime,
  filter,
  switchMap,
  firstValueFrom
} from 'rxjs';
import { GoogleMapsApiService } from '@core/services';
import { IAutocompleteResponse, IPlacePrediction } from '@core/interface';
import { getCountryISOAlfa2 } from '@core/utils';

@Component({
  selector: 'lib-input-address-autosuggest',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './input-address-autosuggest.component.html',
  styleUrls: ['./input-address-autosuggest.component.scss'],
})
export class InputAddressAutosuggestComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly googleMapsApiService = inject(GoogleMapsApiService);
  private readonly translate = inject(TranslateService);

  selectedLatLng = output<{ lat: number; lng: number } | null>();

  // showFinder controla se as sugestões serão exibidas. Usamos a função #handleShowFinder para alterar seu estado
  showFinder = signal(false);
  isSearching = signal(false);
  suggestions = signal<{ placePrediction: IPlacePrediction }[]>([]);

  addressSearchForm!: FormGroup;
  finder$!: Observable<IAutocompleteResponse>;
  private enableSearch = true;

  ngOnInit(): void {
    this.#initializeForm();
    this.#initializeSearchEngine();
  }

  #initializeForm(): void {
    this.addressSearchForm = this.fb.group({
      control: [''],
    });
  }

  #initializeSearchEngine(): void {
    this.finder$ = this.addressSearchForm.get('control')?.valueChanges.pipe(
      debounceTime(300),
      filter((text: string) => {
        return Boolean(text && text.length > 2 && this.enableSearch);
      }),
      switchMap((text: string) => {
        this.isSearching.set(true);
        this.#handleShowFinder(true);

        const regionCode = getCountryISOAlfa2(sessionStorage.getItem('country') as string);
        return this.googleMapsApiService.autocompletePlaces(text, regionCode, this.translate.currentLang);
      })
    ) as Observable<IAutocompleteResponse>;

    this.finder$.subscribe(response => {
      if (response?.suggestions) {
        this.suggestions.set(response.suggestions);
      }
    });
  }

  async getSuggest(place: IPlacePrediction): Promise<void> {
    const { text, placeId } = place;

    this.enableSearch = false;

    this.addressSearchForm.get('control')?.setValue(text.text);

    try {
      const placeDetailsV2 = await firstValueFrom(this.googleMapsApiService.placeDetailsV2(placeId));
      this.#handleShowFinder(false);

      if (placeDetailsV2) {
        const coordinates: { lat: number; lng: number } = {
          lat: placeDetailsV2.geometry.location.lat,
          lng: placeDetailsV2.geometry.location.lng,
        };
        this.selectedLatLng.emit(coordinates);
      }
    } catch (error) {
      this.selectedLatLng.emit(null);
    }

    this.enableSearch = true;
  }

  // Usado para localização via browser
  getUserLocation(): void {
    if (navigator.geolocation) {
      this.enableSearch = false;
      this.#handleShowFinder(false);
      this.suggestions.set([]);

      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const coordinates: { lat: number; lng: number } = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.#getCurrentAddressText(coordinates);
          this.selectedLatLng.emit(coordinates);
        },
        (error) => {
          console.error('Geolocation error:', error);
          this.enableSearch = true;
          this.selectedLatLng.emit(null);
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser');
      this.selectedLatLng.emit(null);
    }
  }

  // o Gmaps não retorna o endereço em texto pelo revgeocode, então criei esse método que chama
  // outro endpoint que retorna essa informação e a utiliza para popular o input de busca de endereço
  #getCurrentAddressText(coordinates: { lat: number; lng: number }): void {
    const latlngString = `${coordinates.lat},${coordinates.lng}`;

    this.googleMapsApiService.geocodeBy('latlng', latlngString).subscribe({
      next: (response) => {
        if (response.results && response.results.length > 0) {
          const address = response.results[0].formatted_address;
          this.addressSearchForm.get('control')?.setValue(address, { emitEvent: false });
        }
        this.enableSearch = true;
      },
      error: (error) => {
        console.error('Geocoding error:', error);
        this.addressSearchForm.get('control')?.setValue(`${coordinates.lat}, ${coordinates.lng}`, { emitEvent: false });
        this.enableSearch = true;
      }
    });
  }

  clearAddressInput(): void {
    this.addressSearchForm.get('control')?.setValue('');
    this.#handleShowFinder(false);
    this.suggestions.set([]);
    this.selectedLatLng.emit(null);
  }

  containInputValue(field: string): boolean {
    return this.addressSearchForm.get(field)?.value !== '';
  }

  // Define a exibição dos botões contendo suggestions de endereço
  #handleShowFinder(show = true): void {
    this.showFinder.set(show);
  }
}
